import React from "react";
import { Link } from "react-router-dom";
import ProjectsSection from "../../components/ProjectsSection";
import Testimonials from "../../components/Testimonials";

const WebDevelopment = () => {

  // ✅ REAL PROJECTS (LIVE LINKS)
  const projects = [
    {
      title: "Shared Solutions",
      desc: "Professional business website delivering scalable digital solutions.",
      link: "https://sharedsolutions.online",
    },
    {
      title: "Shared Restaurant Solutions",
      desc: "Platform tailored for restaurant management and growth.",
      link: "https://www.sharedrestaurantsolutions.com",
    },
    {
      title: "Kostastina",
      desc: "Elegant brand website focused on premium user experience.",
      link: "https://kostastina.com",
    },
    {
      title: "Systemsity",
      desc: "Modern IT solutions platform showcasing digital innovation.",
      link: "https://www.systemsity.tech",
    },
    {
      title: "E-commerce Platform",
      desc: "Fully functional e-commerce website with modern UI and cart system.",
      link: "https://e-commerce-website-gamma-pied.vercel.app/",
    },
    {
      title: "Landing Page Project",
      desc: "High-converting landing page designed for marketing campaigns.",
      link: "https://landing-page-nine-lac-43.vercel.app/",
    },
    {
      title: "TheLogicWaves",
      desc: "Official company website showcasing services and portfolio.",
      link: "https://www.thelogicwaves.com",
    },
  ];

  // ✅ SERVICE-SPECIFIC TESTIMONIALS
  const testimonials = [
    {
      name: "Michael Roberts",
      role: "Business Owner",
      feedback:
        "TheLogicWaves delivered a powerful website that boosted our online presence significantly.",
    },
    {
      name: "Sophia Martinez",
      role: "Restaurant Founder",
      feedback:
        "Their team understood our needs perfectly and built a seamless digital experience.",
    },
    {
      name: "Daniel Carter",
      role: "Startup Founder",
      feedback:
        "Professional, fast, and highly skilled — highly recommended for web development.",
    },
  ];

  return (
    <div className="text-white bg-black overflow-hidden">

      {/* HERO */}
      <section className="relative min-h-screen flex items-center justify-center text-center px-6">
        <div className="absolute w-[500px] h-[500px] bg-purple-600/30 blur-[120px] rounded-full top-20 left-1/2 -translate-x-1/2"></div>

        <div className="relative z-10 max-w-4xl">
          <h1 className="text-5xl md:text-7xl font-extrabold">
            We Build <span className="text-purple-400">High-Impact</span><br />
            Digital Experiences
          </h1>

          <p className="mt-6 text-gray-300 text-lg md:text-xl max-w-2xl mx-auto">
            Not just websites — we create scalable, high-performing platforms
            designed to convert visitors into customers.
          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="px-8 py-4 bg-purple-600 rounded-full font-semibold hover:scale-105 transition shadow-lg"
            >
              Start Your Project
            </Link>

            <a
              href="https://calendly.com/thelogicwaves/30min"
              target="_blank"
              rel="noreferrer"
              className="px-8 py-4 border border-white/20 rounded-full hover:bg-white/10 transition"
            >
              Book a Strategy Call
            </a>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-24 px-6 max-w-5xl mx-auto text-center">
        <h2 className="text-4xl font-bold mb-8">More Than Just Development</h2>
        <p className="text-gray-300 leading-relaxed">
          We don’t just write code — we build digital products that solve
          real business problems with performance, scalability, and UX at the core.
        </p>
      </section>

      {/* FEATURES */}
      <section className="py-24 px-6">
        <h2 className="text-4xl font-bold text-center mb-16">What Makes Us Different</h2>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {[
            "Performance First",
            "Scalable Architecture",
            "Conversion Focused",
            "Clean Code",
            "SEO Ready",
            "Modern UI",
          ].map((title, i) => (
            <div key={i} className="bg-white/5 p-6 rounded-2xl border border-white/10 hover:scale-105 transition">
              <h3 className="text-xl font-semibold mb-2 text-purple-400">{title}</h3>
              <p className="text-gray-400 text-sm">
                High-quality execution tailored for real business impact.
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 px-6 bg-[#0b1a35] text-center">
        <h2 className="text-4xl font-bold mb-16">Our Proven Process</h2>

        <div className="grid md:grid-cols-4 gap-10 max-w-6xl mx-auto">
          {["Discovery", "Strategy", "Development", "Launch"].map((step, i) => (
            <div key={i}>
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-purple-600 text-xl font-bold">
                {i + 1}
              </div>
              <p className="text-gray-300">{step}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TECH STACK */}
      <section className="py-24 px-6 text-center">
        <h2 className="text-4xl font-bold mb-12">Tech Stack We Use</h2>

        <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
          {["React", "Next.js", "Node.js", "MongoDB", "Tailwind", "Firebase"].map((tech, i) => (
            <span key={i} className="px-5 py-2 bg-white/10 rounded-full text-sm hover:bg-purple-600/30 transition">
              {tech}
            </span>
          ))}
        </div>
      </section>

      {/* 🔥 PROJECTS SECTION */}
      <section className="pt-10">
        <h2 className="text-4xl font-bold text-center mb-4">
          Our <span className="text-purple-400">Work</span>
        </h2>
        <p className="text-center text-gray-400 mb-12">
          Real projects we’ve successfully delivered
        </p>

        <ProjectsSection projects={projects} />
      </section>

     <Testimonials testimonials={testimonials} />
     
      {/* CTA */}
      <section className="py-28 text-center bg-gradient-to-r from-purple-600 to-pink-500 px-6">
        <h2 className="text-4xl md:text-5xl font-bold mb-6">
          Let’s Build Something Powerful
        </h2>

        <p className="text-gray-200 mb-10 max-w-xl mx-auto">
          Ready to take your business to the next level?
        </p>

        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/contact" className="px-8 py-4 bg-black rounded-full font-semibold hover:scale-105 transition">
            Contact Us
          </Link>

          <a href="https://wa.me/+923009609342" className="px-8 py-4 border rounded-full">
            WhatsApp Us
          </a>
        </div>
      </section>

    </div>
  );
};

export default WebDevelopment;